import { CaseStatus } from '@/features/air/case-management/types'

import { openPresets } from '@/components/common/pill/preset'

import type { PillPreset } from '@/components/common/pill/preset'

/**
 * The one owner of case-status labels and colours (ADR 0020 §5).
 *
 * Lives at the AIR **Platform** root, not inside `case-management/`, because
 * three AIR subfeatures render it — case management, the alerts overview widget
 * and the multi-tenant MSP table. A-02 refinements 1–2 make subfeature → `air/*`
 * a within-Platform import, so this is their legal shared home; `shared/` would
 * be wrong, since a Case is an AIR concept and Graylog's Rule→Alert shell must
 * never merge Platform types (ADR 0001).
 *
 * Replaces `getStatusVariant`, which lived inside a table hook and was reached
 * into from two other subfeatures.
 */

/**
 * Annotated `Record<CaseStatus, …>` so `tsc` fails on a missing member the day
 * the `z.enum` gains one — ADR 0020 §5's closed-union clause.
 */
const PRESETS: Record<CaseStatus, PillPreset> = {
	'open': { label: 'Open', tone: 'success' },
	'acknowledged': { label: 'Acknowledged', tone: 'alert' },
	'closed': { label: 'Closed', tone: 'neutral' },
	'auto-resolved': { label: 'Auto-resolved', tone: 'pending' },
}

/**
 * Opened for lookup on top of that exhaustive record — the second half of ADR
 * 0020 §5's idiom. `alert_status` is a `VARCHAR` with the enum enforced only in
 * application code, so a surprise value must render a grey pill, not a blank
 * cell (P-17).
 */
export const CASE_STATUS_PRESETS = openPresets('case status', PRESETS)

/**
 * The same record driving the **Status filter** dropdown, so a case cannot read
 * "Auto-resolved" in the table and "Auto-Resolved" in the control that filters
 * it (ADR 0020 §5). `CaseStatus` stays the authority on membership, `PRESETS` on
 * display — and indexing it here is type-safe, since `value` is a union member
 * by construction.
 */
export const CASE_STATUS_FILTER_OPTIONS = CaseStatus.options.map((value) => ({
	value,
	label: PRESETS[value].label,
}))
